import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Pencil, RefreshCw, Trash2, Upload, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  kind: "episode",
  title: "",
  description: "",
  excerpt: "",
  body: "",
  youtube_url: "",
  featured_image: "",
  external_url: "",
  topics: "",
  featured: false,
  order: 0,
  published: true,
  publish_at: "",
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const inputCls =
  "w-full rounded-2xl border border-forest/20 bg-white/70 px-4 py-2.5 text-sm outline-none focus:border-gold";

const ItemEditor = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/resources/upload", fd);
      setF((v) => ({ ...v, featured_image: data.url }));
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Upload failed."));
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...f,
      topics: f.topics.split(",").map((t) => t.trim()).filter(Boolean),
      order: Number(f.order) || 0,
      publish_at: f.publish_at || null,
      published_at: null,
    };
    try {
      if (f.item_id) {
        await api.put(`/admin/resources/media/${f.item_id}`, payload);
      } else {
        await api.post("/admin/resources/media", payload);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div data-testid="media-editor" className="mt-4 flex flex-col gap-3 rounded-2xl border border-gold/30 bg-sage/40 p-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input data-testid="media-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} />
        {f.kind === "episode" ? (
          <input data-testid="media-youtube-url" placeholder="YouTube video link" value={f.youtube_url} onChange={(e) => setF({ ...f, youtube_url: e.target.value })} className={inputCls} />
        ) : (
          <input data-testid="media-external-url" placeholder="Link to original post (optional)" value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} className={inputCls} />
        )}
      </div>
      <textarea data-testid="media-description" rows={2} placeholder={f.kind === "episode" ? "Episode description (optional override)" : "Short excerpt"} value={f.kind === "episode" ? f.description : f.excerpt} onChange={(e) => setF(f.kind === "episode" ? { ...f, description: e.target.value } : { ...f, excerpt: e.target.value })} className={inputCls} />
      {f.kind === "post" && (
        <>
          <textarea data-testid="media-body" rows={8} placeholder="Write the article. Blank lines separate paragraphs." value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={`${inputCls} rounded-2xl`} />
          <div className="flex items-center gap-3">
            <button data-testid="media-image-upload" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-4 py-2 text-xs font-semibold text-forest hover:border-gold">
              <Upload className="h-3.5 w-3.5" />
              {f.featured_image ? "Replace featured image" : "Upload featured image"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
            {f.featured_image && <span className="text-xs text-forest">Image attached</span>}
          </div>
          <label className="flex items-center gap-2 text-xs text-taupe">
            Schedule publication (optional)
            <input data-testid="media-publish-at" type="datetime-local" value={f.publish_at || ""} onChange={(e) => setF({ ...f, publish_at: e.target.value })} className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs outline-none focus:border-gold" />
          </label>
        </>
      )}
      <input data-testid="media-topics" placeholder="Topics, comma separated (e.g. Gut Health, Rest)" value={f.topics} onChange={(e) => setF({ ...f, topics: e.target.value })} className={inputCls} />
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="media-featured" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="brand-check h-4 w-4" />
          Featured
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="media-published" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="brand-check h-4 w-4" />
          Published
        </label>
        <label className="flex items-center gap-2 text-xs text-taupe">
          Display order
          <input data-testid="media-order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className="w-20 rounded-full border border-forest/20 bg-white/70 px-3 py-1.5 text-xs outline-none focus:border-gold" />
        </label>
      </div>
      <div className="flex gap-3">
        <button data-testid="media-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-semibold text-ivory hover:bg-forest-deep disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Save
        </button>
        <button onClick={onCancel} className="text-xs font-semibold text-taupe underline-offset-4 hover:underline">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default function AdminTerrain() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("episode");
  const [channelUrl, setChannelUrl] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/resources/media").then(({ data }) => {
    setData(data);
    setChannelUrl(data.channel?.channel_url || "");
  });

  useEffect(() => {
    document.title = "Terrain and Truth | Rooted Healing Admin";
    load().catch(() => setData({ items: [], channel: {}, topics: [] }));
  }, []);

  const saveChannel = async () => {
    try {
      await api.put("/admin/resources/youtube-channel", { channel_url: channelUrl });
      toast.success("Channel saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/resources/youtube/sync");
      if (data.status === "ok") {
        toast.success(`Sync complete. ${data.imported} new episode${data.imported === 1 ? "" : "s"} imported.`);
      } else {
        toast.error(`Sync ${data.status.replace("_", " ")}. Check the channel URL.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSyncing(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this content from the website?")) return;
    await api.delete(`/admin/resources/media/${id}`);
    toast.success("Removed.");
    load();
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  const items = data.items.filter((i) => i.kind === tab);

  return (
    <div data-testid="admin-terrain">
      <h1 className="font-serif text-3xl text-forest">Terrain and Truth Content Manager</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Manage podcast episodes from YouTube and your written posts in one place. Videos are
        embedded from YouTube, never rehosted.
      </p>

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
        <div className="flex items-center gap-2">
          <Youtube className="h-4 w-4 text-forest" />
          <h2 className="font-serif text-xl text-forest">YouTube channel</h2>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            data-testid="channel-url-input"
            value={channelUrl}
            onChange={(e) => setChannelUrl(e.target.value)}
            placeholder="https://www.youtube.com/@yourchannel or channel ID"
            className="min-w-72 flex-1 rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
          />
          <button data-testid="channel-save" onClick={saveChannel} className="rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory hover:bg-forest-deep">
            Save Channel
          </button>
          <button
            data-testid="channel-sync"
            onClick={sync}
            disabled={syncing || !channelUrl}
            className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest hover:border-gold disabled:opacity-50"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync Now
          </button>
        </div>
        <p className="mt-3 text-xs text-taupe">
          {data.channel?.channel_id ? (
            <>
              Channel ID: <span className="font-mono text-forest">{data.channel.channel_id}</span>
              {" · "}RSS:{" "}
              <a
                href={data.channel.rss_url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-forest underline-offset-2 hover:underline"
              >
                {data.channel.rss_url}
              </a>
              {data.channel.last_sync && <> · Last synced {fmt(data.channel.last_sync)}</>}
              {" · "}Sync imports only newly published public videos.
            </>
          ) : (
            "Sync uses the channel's public feed. No API key is required; the YouTube API key field in Integrations can stay empty until you need it."
          )}
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {[
          { key: "episode", label: "Podcast Episodes" },
          { key: "post", label: "Posts & Articles" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`media-tab-${t.key}`}
            onClick={() => {
              setTab(t.key);
              setAdding(false);
              setEditing(null);
            }}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === t.key ? "bg-forest text-ivory" : "border border-forest/20 text-forest hover:border-gold"
            }`}
          >
            {t.label}
          </button>
        ))}
        <button
          data-testid="media-add"
          onClick={() => {
            setAdding(true);
            setEditing(null);
          }}
          className="rounded-full border border-gold/50 px-5 py-2.5 text-sm font-semibold text-gold-dark hover:bg-gold/10"
        >
          {tab === "episode" ? "+ Add YouTube video" : "+ Write a post"}
        </button>
      </div>

      {adding && (
        <ItemEditor
          initial={{ ...EMPTY, kind: tab }}
          onSaved={() => {
            setAdding(false);
            load();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="mt-6 flex flex-col gap-3">
        {items.length === 0 && !adding && (
          <p className="rounded-2xl border border-forest/10 bg-white/75 p-8 text-center text-sm text-taupe">
            {tab === "episode"
              ? "No episodes yet. Save your channel and press Sync Now, or add a video manually."
              : "No posts yet. Write your first article or link to one published elsewhere."}
          </p>
        )}
        {items.map((item) => (
          <div key={item.item_id} data-testid={`media-row-${item.item_id}`} className="rounded-2xl border border-forest/10 bg-white/75 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-semibold text-forest">{item.title}</p>
                <p className="mt-0.5 text-xs text-taupe">
                  {fmt(item.published_at)}
                  {item.source === "youtube-sync" && " · from YouTube"}
                  {item.topics?.length > 0 && ` · ${item.topics.join(", ")}`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${item.published ? "bg-sage text-forest" : "bg-charcoal/10 text-taupe"}`}>
                  {item.published ? "published" : "hidden"}
                </span>
                {item.featured && (
                  <span className="rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gold-dark">
                    featured
                  </span>
                )}
                <button
                  data-testid={`media-edit-${item.item_id}`}
                  onClick={() => setEditing(editing === item.item_id ? null : item.item_id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest hover:border-gold"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  data-testid={`media-delete-${item.item_id}`}
                  onClick={() => remove(item.item_id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-900/20 px-4 py-2 text-xs font-semibold text-red-900/70 hover:border-red-900/50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            {editing === item.item_id && (
              <ItemEditor
                initial={{
                  ...EMPTY,
                  ...item,
                  topics: (item.topics || []).join(", "),
                  publish_at: item.publish_at || "",
                }}
                onSaved={() => {
                  setEditing(null);
                  load();
                }}
                onCancel={() => setEditing(null)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
